import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, ChevronUp, type LucideIcon } from "lucide-react";
import { NAV_PLAN_COPY } from "@fomofi/shared";
import { cn } from "@/lib/utils";
import {
  PLAN_GROUP_ID,
  inPlan,
  planGroupLinks,
  planGroupOpenFor,
  planGroupToggle,
  planLinkIsCurrent,
} from "@/lib/planNavGroup";

/**
 * Package §7 (NavDemo.dc.html): Plan as an expandable group, in the expanded sidebar and
 * the tablet drawer. The parent is a button (aria-expanded, aria-controls="plan-links")
 * that opens the group and goes to Your plan; its entries are links, indented 52px, at
 * least 44px tall. The rules are lib/planNavGroup's.
 *
 * Debt Eliminator has no badge (Debt Eliminator Spec 1.2 §4.1; Paul, 2026-10-31). It was
 * "Debt payoff" with the count of debts in the payoff plan (C4).
 *
 * The parent keeps its siblings' size and shape (RedesignNavItem), so the four items read
 * as one list; the board's colours, indent and target sizes are the group's own.
 */
export function PlanNavGroup({ icon: Icon }: { icon: LucideIcon }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(() => planGroupOpenFor(pathname));
  // A route change sets the group: open on a Plan page, closed anywhere else.
  useEffect(() => {
    setOpen(planGroupOpenFor(pathname));
  }, [pathname]);
  const inside = inPlan(pathname);

  return (
    <>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={PLAN_GROUP_ID}
        data-plan-group=""
        onClick={() => {
          const next = planGroupToggle(open);
          setOpen(next.open);
          if (next.goTo && pathname !== next.goTo) navigate(next.goTo);
        }}
        className={cn(
          "pkg-focus flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium transition-colors duration-150",
          inside
            ? "bg-[var(--nav-plan-parent)] text-white"
            : "text-sidebar-foreground/60 hover:bg-sidebar-accent hover:text-sidebar-foreground",
        )}
      >
        <Icon className="h-[18px] w-[18px] shrink-0" strokeWidth={inside ? 2 : 1.75} />
        <span className="flex-1">{NAV_PLAN_COPY}</span>
        {open ? (
          <ChevronUp className="h-4 w-4 shrink-0" aria-hidden="true" />
        ) : (
          <ChevronDown className="h-4 w-4 shrink-0" aria-hidden="true" />
        )}
      </button>
      {open && (
        <ul id={PLAN_GROUP_ID} role="list" className="mt-0.5 flex flex-col gap-0.5 pb-1.5">
          {planGroupLinks().map((section) => {
            const current = planLinkIsCurrent(section, pathname);
            return (
              <li key={section.to}>
                <Link
                  to={section.to}
                  aria-current={current ? "page" : undefined}
                  data-plan-link=""
                  className={cn(
                    "pkg-focus flex min-h-11 items-center gap-2 rounded-[10px] py-2.5 pr-3.5 pl-[3.25rem] text-[0.9375rem] transition-colors duration-150",
                    current
                      ? "bg-[var(--nav-plan-selected)] font-semibold text-white"
                      : "text-sidebar-foreground/60 hover:bg-sidebar-accent hover:text-sidebar-foreground",
                  )}
                >
                  <span className="min-w-0 flex-1">{section.label}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}
